import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationPreferences, WebPushSetup } from '@/components/notifications';
import { SHOW_NOTIFICATION_SETTINGS } from '@/lib/constants';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { resetOnboarding, resetTour } from '@/components/onboarding';
import { RoleGuard } from '@/components/layout/RoleGuard';
import { ProductsSettings } from '@/components/products/ProductsSettings';
import { WaterFieldsEditor } from '@/components/settings/WaterFieldsEditor';
import { ALERT_LIMITS_HELP, ALERT_LIMITS_TITLE } from '@/components/alerts/alertLimitsText';
import { ParameterThresholdsEditor } from '@/components/settings/ParameterThresholdsEditor';
import { FeedingScheduleEditor } from '@/components/settings/FeedingScheduleEditor';
import { ApplicationBundlesEditor } from '@/components/settings/ApplicationBundlesEditor';
import { WaterColorEditor } from '@/components/settings/WaterColorEditor';
import { useMembership } from '@/hooks/useMembership';
import { SurvivalSampleSizeEditor } from '@/components/settings/SurvivalSampleSizeEditor';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { api } from '@/lib/api';
import { PlayCircle, RotateCcw, Check } from 'lucide-react';
import type { AlertThresholds, FarmResponse } from '@/types/api';

export const Route = createFileRoute('/_app/settings')({
  component: () => (
    <RoleGuard allowedRoles={['supervisor', 'owner', 'operator']}>
      <SettingsPage />
    </RoleGuard>
  ),
});

const DEFAULT_THRESHOLDS: AlertThresholds = {
  ph_min: 7.5,
  ph_max: 8.5,
  do_min: 4.0,
  do_max: 8.0,
  temp_min: 26,
  temp_max: 32,
  ammonia_max: 0.1,
  nitrite_max: 0.5,
  salinity_min: 15,
  salinity_max: 30,
};

function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { farmId } = useCurrentFarm();
  const { isOwnerOrAbove } = useMembership();
  const queryClient = useQueryClient();

  const { data: farmData } = useQuery({
    queryKey: ['farm', farmId],
    queryFn: () => api.get<{ data: FarmResponse }>(`/api/v1/farms/${farmId}`),
    enabled: !!farmId,
    staleTime: 5 * 60 * 1000,
  });

  const savedThresholds = farmData?.data?.settings?.alert_thresholds;
  const serverThresholds = { ...DEFAULT_THRESHOLDS, ...savedThresholds };

  // Local edits overlay server thresholds; reset on save
  const [localEdits, setLocalEdits] = useState<Partial<AlertThresholds>>({});
  const [saveSuccess, setSaveSuccess] = useState(false);

  const thresholds: AlertThresholds = { ...serverThresholds, ...localEdits };

  // Issue #114: surface validation errors instead of silently dropping bad input.
  // The previous updateField() ignored non-numeric values and accepted min>max
  // ranges, letting users save nonsense that broke the alert engine.
  const validationErrors: Partial<Record<keyof AlertThresholds, string>> = {};
  const MIN_MAX_PAIRS: Array<[keyof AlertThresholds, keyof AlertThresholds]> = [
    ['ph_min', 'ph_max'],
    ['do_min', 'do_max'],
    ['temp_min', 'temp_max'],
    ['salinity_min', 'salinity_max'],
  ];
  for (const [minKey, maxKey] of MIN_MAX_PAIRS) {
    const minV = thresholds[minKey];
    const maxV = thresholds[maxKey];
    if (typeof minV === 'number' && typeof maxV === 'number' && minV >= maxV) {
      validationErrors[minKey] = `Must be less than ${maxKey.replace('_', ' ')}`;
      validationErrors[maxKey] = `Must be greater than ${minKey.replace('_', ' ')}`;
    }
  }
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const saveMutation = useMutation({
    mutationFn: () =>
      api.put(`/api/v1/farms/${farmId}/settings`, {
        alert_thresholds: thresholds,
      }),
    onSuccess: () => {
      setLocalEdits({});
      queryClient.invalidateQueries({ queryKey: ['farm', farmId] });
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const updateField = (field: keyof AlertThresholds, value: string) => {
    // Empty string: clear the edit so the displayed value falls back to server default.
    if (value === '') {
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    const num = parseFloat(value);
    if (!isNaN(num)) {
      setLocalEdits((prev) => ({ ...prev, [field]: num }));
    }
    // Non-numeric input is ignored silently at the input level; the
    // cross-field validation above still runs on whatever value is current.
  };

  const handleReplayTour = () => {
    resetTour();
    navigate({ to: '/overview' });
  };

  const handleRestartOnboarding = () => {
    resetOnboarding();
    window.location.reload();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure account and notification settings
        </p>
      </div>

      {/* Account Info */}
      <Card>
        <Card.Header>
          <Card.Title>Account</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-gray-900">{user?.email || 'Not available'}</p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {farmId && <ProductsSettings key={farmId} farmId={farmId} />}

      {farmId && <WaterFieldsEditor key={`water-${farmId}`} farmId={farmId} canEdit={isOwnerOrAbove} />}

      {farmId && <ParameterThresholdsEditor key={`thresholds-${farmId}`} farmId={farmId} canEdit={isOwnerOrAbove} />}
      {farmId && <FeedingScheduleEditor key={`feeding-${farmId}`} farmId={farmId} canEdit={isOwnerOrAbove} />}

      {farmId && <ApplicationBundlesEditor key={`bundles-${farmId}`} farmId={farmId} canEdit={isOwnerOrAbove} />}

      {farmId && <WaterColorEditor key={`water-colors-${farmId}`} farmId={farmId} canEdit={isOwnerOrAbove} />}

      {farmId && <SurvivalSampleSizeEditor key={`survival-sample-${farmId}`} farmId={farmId} canEdit={isOwnerOrAbove} />}

      {SHOW_NOTIFICATION_SETTINGS && (
        <>
          <WebPushSetup />
          <NotificationPreferences />
        </>
      )}

      {/* Help & Tour */}
      <Card>
        <Card.Header>
          <Card.Title>Help & Tour</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Replay Feature Tour</p>
                <p className="text-sm text-gray-500">
                  Take another look at the key features
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReplayTour}
                leftIcon={<PlayCircle size={16} />}
              >
                Start Tour
              </Button>
            </div>
            <div className="border-t pt-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Restart Welcome Flow</p>
                <p className="text-sm text-gray-500">
                  See the welcome introduction again
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestartOnboarding}
                leftIcon={<RotateCcw size={16} />}
              >
                Restart
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Alert Thresholds */}
      <Card>
        <Card.Header>
          <Card.Title>{ALERT_LIMITS_TITLE}</Card.Title>
          <Card.Description>{ALERT_LIMITS_HELP}</Card.Description>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ThresholdRangeInput
                label="pH Range"
                minValue={thresholds.ph_min}
                maxValue={thresholds.ph_max}
                step={0.1}
                onMinChange={(v) => updateField('ph_min', v)}
                onMaxChange={(v) => updateField('ph_max', v)}
              />
              <ThresholdRangeInput
                label="DO Range (mg/L)"
                minValue={thresholds.do_min}
                maxValue={thresholds.do_max}
                step={0.1}
                onMinChange={(v) => updateField('do_min', v)}
                onMaxChange={(v) => updateField('do_max', v)}
              />
              <ThresholdRangeInput
                label="Temperature (°C)"
                minValue={thresholds.temp_min}
                maxValue={thresholds.temp_max}
                step={0.5}
                onMinChange={(v) => updateField('temp_min', v)}
                onMaxChange={(v) => updateField('temp_max', v)}
              />
              <ThresholdRangeInput
                label="Salinity (ppt)"
                minValue={thresholds.salinity_min}
                maxValue={thresholds.salinity_max}
                step={1}
                onMinChange={(v) => updateField('salinity_min', v)}
                onMaxChange={(v) => updateField('salinity_max', v)}
              />
              <ThresholdMaxInput
                label="Ammonia Max (ppm)"
                value={thresholds.ammonia_max}
                step={0.01}
                onChange={(v) => updateField('ammonia_max', v)}
              />
              <ThresholdMaxInput
                label="Nitrite Max (ppm)"
                value={thresholds.nitrite_max}
                step={0.01}
                onChange={(v) => updateField('nitrite_max', v)}
              />
            </div>
            <div className="pt-4 border-t flex items-center gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || hasValidationErrors}
                leftIcon={saveSuccess ? <Check size={16} /> : undefined}
              >
                {saveMutation.isPending ? 'Saving...' : saveSuccess ? 'Saved' : 'Save alert limits'}
              </Button>
              {hasValidationErrors && (
                <span className="text-sm text-red-600">
                  Fix range errors before saving (min must be less than max).
                </span>
              )}
              {!hasValidationErrors && saveMutation.isError && (
                <span className="text-sm text-red-600">
                  Failed to save. Please try again.
                </span>
              )}
              {!hasValidationErrors && !saveSuccess && !saveMutation.isError && (
                <span className="text-sm text-gray-500">
                  Alerts will trigger when readings fall outside these ranges
                </span>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

function ThresholdRangeInput({
  label,
  minValue,
  maxValue,
  step,
  onMinChange,
  onMaxChange,
}: {
  label: string;
  minValue?: number;
  maxValue?: number;
  step: number;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          step={step}
          value={minValue ?? ''}
          onChange={(e) => onMinChange(e.target.value)}
          className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <span className="text-gray-400 text-sm">to</span>
        <input
          type="number"
          step={step}
          value={maxValue ?? ''}
          onChange={(e) => onMaxChange(e.target.value)}
          className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </div>
  );
}

function ThresholdMaxInput({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value?: number;
  step: number;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="mt-1">
        <input
          type="number"
          step={step}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </div>
  );
}
